import { createClient, SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import { Platform } from '../../utils/platform';
import AsyncStorage from '@react-native-async-storage/async-storage';

declare global {
  interface Window {
    __ENV__?: Record<string, string>;
    __SUPABASE_CLIENT__?: SupabaseClient;
  }
  var __ENV__: Record<string, string> | undefined;
}

// Helper function to get environment variables
function getEnv(key: string): string {
  // For web
  if (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__[key]) {
    return window.__ENV__[key] as string;
  }
  
  // For React Native
  if (typeof global !== 'undefined' && global.__ENV__ && global.__ENV__[key]) {
    return global.__ENV__[key] as string;
  }
  
  // For Node.js
  if (typeof process !== 'undefined' && process.env && process.env[`EXPO_PUBLIC_${key}`]) {
    return process.env[`EXPO_PUBLIC_${key}`] as string;
  }
  
  return '';
}

// Get Supabase URL and anon key from environment
const supabaseUrl = getEnv('SUPABASE_URL') || 'https://eneiufxbxiltaoczjybq.supabase.co';
const supabaseAnonKey = getEnv('SUPABASE_ANON_KEY') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVuZWl1ZnhieGlsdGFvY3pqeWJxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE1MTMwMzIsImV4cCI6MjA1NzA4OTAzMn0.XjwFg8iMhJsMsuBzbCpym7iZwbfbp_9_AkHWcyS_Hwc';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase URL or anon key in environment');
}

let supabaseClient: SupabaseClient;

// Check if we're in a web environment and if there's already a Supabase client
if (typeof window !== 'undefined' && window.__SUPABASE_CLIENT__) {
  console.log('Using existing Supabase client from window object');
  supabaseClient = window.__SUPABASE_CLIENT__;
} else if (Platform.isNative) {
  // For React Native, use AsyncStorage
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      storage: AsyncStorage,
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false
    }
  });
} else {
  // For web, use localStorage
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  });
  
  // Store the client in the window object for reuse
  if (typeof window !== 'undefined') {
    window.__SUPABASE_CLIENT__ = supabaseClient;
  }
}

export const supabase = supabaseClient;

// Re-export PostgrestError from supabase-js
export { PostgrestError };

// Auth helper functions
export async function signUp(email: string, password: string) {
  return supabase.auth.signUp({ email, password });
}

export async function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signInWithGoogle() {
  return supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });
}

export async function signInWithApple() {
  return supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });
}

export async function resetPassword(email: string) {
  let options = {};
  
  // Only add redirectTo for web platforms, not for React Native
  if (typeof window !== 'undefined' && !Platform.isNative) {
    options = {
      redirectTo: `${window.location.origin}/auth/reset-password`
    };
  }
  
  return supabase.auth.resetPasswordForEmail(email, options);
}

export async function updatePassword(password: string) {
  return supabase.auth.updateUser({ password });
}

export async function signOut() {
  return supabase.auth.signOut();
}

export async function getUserSession() {
  return supabase.auth.getSession();
}

export async function getUser() {
  const { data } = await supabase.auth.getUser();
  return data.user;
}

// Storage helper functions
export function getProfileImageUrl(userId: string, path: string) {
  const { data } = supabase.storage
    .from('profiles')
    .getPublicUrl(`${userId}/profile/${path}`);
  
  return data.publicUrl;
}

export async function uploadProfileImage(userId: string, file: File | Blob) {
  const fileExt = getFileExtension(file);
  const filePath = `${userId}/profile/avatar${fileExt}`;
  
  const { data, error } = await supabase.storage
    .from('profiles')
    .upload(filePath, file, {
      upsert: true
    });
  
  if (error) {
    throw error;
  }
  
  return getProfileImageUrl(userId, `avatar${fileExt}`);
}

export async function uploadTransactionAttachment(
  userId: string, 
  transactionId: string,
  file: File | Blob,
  fileName: string
) {
  const filePath = `${userId}/transactions/${transactionId}/${fileName}`;
  
  const { data, error } = await supabase.storage
    .from('attachments')
    .upload(filePath, file, {
      upsert: true
    });
  
  if (error) {
    throw error;
  }
  
  // Get the URL
  const { data: urlData } = supabase.storage
    .from('attachments')
    .getPublicUrl(filePath);
  
  return {
    fileName,
    fileUrl: urlData.publicUrl,
    fileType: file.type,
    uploadDate: new Date().toISOString(),
    size: file instanceof File ? file.size : 0
  };
}

// Helper function to get file extension
function getFileExtension(file: File | Blob): string {
  if (file instanceof File && file.name) {
    const ext = file.name.split('.').pop();
    return ext ? `.${ext}` : '';
  }
  
  // If it's a Blob without a name, try to determine from MIME type
  if (file.type) {
    const mime = file.type.split('/').pop();
    switch (mime) {
      case 'jpeg': return '.jpg';
      case 'png': return '.png';
      case 'pdf': return '.pdf';
      default: return `.${mime}`;
    }
  }
  
  return '';
}

export default supabase; 